import { AssetType } from 'config/consts';
import React, { ComponentProps, useMemo, useState, useEffect } from 'react';
import { addSignCommaFormat } from '../../new-report/utils/formatter';
import { Side } from 'components/features/utils/side';
import {
  AssetTypeKey,
  isNotExistDetailAssetTypes,
} from 'components/features/utils/assetType';
import {
  amountToAmountColor,
  isSymbolEmpty,
} from 'components/features/utils/util';
import Highcharts, { merge } from 'highcharts';
import highchartsStock from 'highcharts/modules/stock';
import HighchartsExporting from 'highcharts/modules/exporting';
import {
  ProfitAndLossStuffingChart,
  ProfitAndLossStuffingChartInternal,
} from './ProfitAndLossStuffingChart';
import { ProfitOrLossItem } from './ProfitOrLossItem';
import {
  AssetHistoryItemsProps,
  ExtraTimeSeriesData,
  TimeSeriesDatum,
  getTopixData,
} from './../assetStuffing/AssetStuffing';
import {
  isHiddenProductTypes,
  toAssetTypeKey,
} from './../PortfolioStuffing/asset/PortfolioStuffingAsset';
import { usePreviewPager } from '../PortfolioStuffing/asset/PortfolioStuffingAssetChart';
import { AssetBundleType } from 'components/features/home/Home';
import { ChartForPreviewType } from 'components/features/preview-share/useChartRenderer';
import {
  usePreview,
  UsePreviewProps,
} from 'components/features/preview-share/Preview';
import {
  RenderedChartToElement,
  ChartPreviewState,
  StateController,
  defualtStateForHome,
  stateToOptions,
} from 'components/features/preview-share/PreviewUtils';
import { isForeignStockTypes } from 'components/features/utils/productTypes';
import { GetUserAssetResult, getAssetHistory, getPositions } from 'config/api';
import { getDate } from 'components/features/utils/converter';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';

highchartsStock(Highcharts);
HighchartsExporting(Highcharts);

export type ProfitAndLossStuffingChartDatum = {
  x: Date;
  y: number | null;
};

export type ProfitAndLossStuffingChartProps = {
  data: ProfitAndLossStuffingChartDatum[];
  extraData?: ExtraTimeSeriesData[];
};

export const ProfitAndLossStuffingChartForPreview: (
  props: ComponentProps<
    ChartForPreviewType<ProfitAndLossStuffingChartProps['data']>
  >
) => React.ReactElement = ({
  data,
  options,
  range,
  setChartDataURL,
  topixData,
}) => {
  return (
    <>
      <ProfitAndLossStuffingChartInternal
        {...{
          data,
          topixData,
          forPreviewSetter: { tag: 'forInPreview', setChartDataURL },
          options: range
            ? merge(options, {
                xAxis: { min: range.min.getTime(), max: range.max.getTime() },
                legend: {
                  enabled: false,
                },
              } as Highcharts.Options)
            : options,
        }}
      />
    </>
  );
};

type ProfitAndLossStuffingProps = {
  asset: GetUserAssetResult;
  assetBundleType: AssetBundleType;
  UserBrokerUpdateTime: React.ReactElement;
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

export type ProfitOrLossItemProps = {
  assetTypeKey: AssetTypeKey;
  side: Side | null;
  link: string | null;
  identifier: string;
  name: string;
  profitOrLossAmount: number | null;
  refresh: { isOutOfDate: boolean; account: string } | null;
  profitOrLossAmountForeign?: number | null;
  profitOrLossAmountForeignUnit?: string;
};

const SORT_METHOD = {
  up: 0,
  down: 1,
} as const;

type SortMethod = typeof SORT_METHOD[keyof typeof SORT_METHOD];

const sortProfitOrLossItem = (method: SortMethod) => (
  items: ProfitOrLossItemProps[]
): ProfitOrLossItemProps[] => {
  const profitOrLossItemPropsToOrd = (p: ProfitOrLossItemProps): number => {
    return p.profitOrLossAmount ?? 0;
  };
  return items.sort((a, b) => {
    return (
      (profitOrLossItemPropsToOrd(a) - profitOrLossItemPropsToOrd(b)) *
      (method === SORT_METHOD.up ? 1 : -1)
    );
  });
};

const positionsToProfitOrLossStuffingProps = (
  state: PositionItemsProps[],
  getIsOutOfDate: (arg: { account: string }) => boolean | null
): ProfitOrLossItemProps[] => {
  return state.map(state => {
    const assetTypeKey = toAssetTypeKey(state);
    const getLink = (state: PositionItemsProps): string | null => {
      if (
        isSymbolEmpty(state.symbol) ||
        isNotExistDetailAssetTypes(assetTypeKey)
      ) {
        return null;
      } else if (
        assetTypeKey === AssetType['投資信託'] &&
        (state.symbol === '' ||
          /^MONO/.test(state.symbol) ||
          /^DC/.test(state.symbol))
      ) {
        return `/accountdetail/${state.account}`;
      } else {
        return `/tradedetail/${state.symbol}`;
      }
    };

    return {
      assetTypeKey,
      side: assetTypeKey === AssetType['信用'] ? (state.side as Side) : null,
      brokerID: state.brokerID,
      identifier: state.symbol,
      name: state.symbolName,
      profitOrLossAmount: state.profitOrLoss,
      link: getLink(state),
      refresh: getIsOutOfDate(state)
        ? { isOutOfDate: true, account: state.account }
        : null,
      profitOrLossAmountForeign:
        assetTypeKey === AssetType['外国株式'] &&
        isForeignStockTypes(state.productType)
          ? state.profitOrLossUSD
          : undefined,
      profitOrLossAmountForeignUnit:
        assetTypeKey === AssetType['外国株式'] &&
        isForeignStockTypes(state.productType)
          ? state.currencyUnit ?? ''
          : undefined,
    };
  });
};

const Total = ({
  profitOrLosss,
}: {
  profitOrLosss: number[];
}): React.ReactElement => {
  const isProfitOrLosssEmpty = profitOrLosss.length === 0;
  const total = isProfitOrLosssEmpty
    ? null
    : profitOrLosss.reduce((acc, v) => acc + v, 0);
  const color = total === 0 || total === null ? '' : amountToAmountColor(total);

  return (
    <>
      <div className="mx-4">
        <div className="text-sm text-gray-700 leading-4 text-right">
          評価損益
        </div>
        <div className="flex items-center">
          <div className="text-base	text-gray-700 font-bold leading-4.5">
            合計
          </div>
          <div
            className={`flex justify-end leading-8 font-bold ml-auto ${color}`}>
            <div className="text-22">{addSignCommaFormat(total)}</div>
            <div className="text-xs ml-1">円</div>
          </div>
        </div>
      </div>
    </>
  );
};

const DropDown = ({
  sortMethod,
  setSortMethod,
}: {
  sortMethod: SortMethod;
  setSortMethod: (value: SortMethod) => void;
}): React.ReactElement => {
  return (
    <>
      <div className="relative ml-auto">
        <select
          className="appearance-none rounded text-xs h-8 w-100p px-2 bg-white border border-gray-400 cursor-pointer"
          value={sortMethod}
          onChange={(e): void => {
            const value = Number(e.target.value);
            if (typeof value === 'number') setSortMethod(value as SortMethod);
          }}>
          <option value={SORT_METHOD.up}>損益昇順</option>
          <option value={SORT_METHOD.down}>損益降順</option>
        </select>
        <div className="absolute pointer-events-none inset-y-0 right-0 flex items-center pr-4">
          <img
            className="w-full"
            src="/static/images/icon/icon-arrow-down.svg"
            height="6"
            width="12"
            alt=""
          />
        </div>
      </div>
    </>
  );
};

type ChartProps = {
  assetHistory: AssetHistoryItemsProps[];
  asset: GetUserAssetResult;
  UserBrokerUpdateTime: React.ReactElement;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

const Chart = ({
  assetHistory,
  asset,
  UserBrokerUpdateTime,
  toPreview,
  backHome,
}: ChartProps): React.ReactElement => {
  const topixData = getTopixData(assetHistory);

  const data = useMemo(
    () =>
      assetHistory.map(asset => {
        return {
          x: getDate(asset.day),
          y: asset.totalProfitOrLoss ?? null,
        };
      }),
    [assetHistory]
  );

  const chartIncorrectData = useMemo(() => {
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: assetHistory.map(it => {
        return {
          value: it.incorrect,
          date: getDate(it.day),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [assetHistory]);

  const props: UsePreviewProps<
    ChartPreviewState,
    ProfitAndLossStuffingChartProps['data']
  > = {
    Chart: ProfitAndLossStuffingChartForPreview,
    defaultState: defualtStateForHome(asset),
    extra: data,
    RenderedChartToElement,
    StateController,
    stateToOptions,
    onCancel: backHome,
    chartTitle: '損益',
  };

  const { preview, TwitterIconPreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  return (
    <>
      <TwitterIconPreviewButton className="bg-bluegray-600" />
      <div className="pl-4 bg-bluegray-600">
        <ProfitAndLossStuffingChart
          {...{ data, extraData: chartIncorrectData, setRange, topixData }}
        />
      </div>
      <div className="flex flex-row items-center relative z-10 -mt-6 mb-4 bg-bluegray-600">
        {UserBrokerUpdateTime}
      </div>
    </>
  );
};

type PoitionPrios = {
  positions: PositionItemsProps[];
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  title: string;
};

const Position = ({
  positions: positions_,
  getIsOutOfDate,
  title,
}: PoitionPrios): React.ReactElement => {
  const [sortMethod, setSortMethod] = useState<SortMethod>(SORT_METHOD.up);

  const positions = positions_
    .filter(item => !isHiddenProductTypes(item.productType))
    .reduce((acc, v): PositionItemsProps[] => {
      const last = acc[acc.length - 1];

      const isSameSymbol =
        last &&
        last.brokerID &&
        last.symbol === v.symbol &&
        last.symbolName === v.symbolName;

      if (isSameSymbol)
        return [
          ...(acc.slice(0, acc.length - 1) as PositionItemsProps[]),
          {
            ...last,
            currentAmount: (last.currentAmount ?? 0) + (v.currentAmount ?? 0),
            profitOrLoss: (last.profitOrLoss ?? 0) + (v.profitOrLoss ?? 0),
            qty: (last.qty ?? 0) + (v.qty ?? 0),
          },
        ];
      return [...acc, v];
    }, [] as PositionItemsProps[]);

  return (
    <>
      <div className="p-4 pb-0 max-w-md mx-auto">
        <div className="flex items-center mb-6">
          <div className="text-lg font-bold">{title}</div>
          <DropDown setSortMethod={setSortMethod} sortMethod={sortMethod} />
        </div>
        <Total
          profitOrLosss={
            positions.map(item => item.profitOrLoss).filter(v => v) as number[]
          }
        />
      </div>
      <div className="border-t border-bluegray-300 mb-4 px-4">
        {sortProfitOrLossItem(sortMethod)(
          positionsToProfitOrLossStuffingProps(positions, getIsOutOfDate)
        ).map((p, i) => (
          <ProfitOrLossItem {...p} key={i} />
        ))}
      </div>
    </>
  );
};

export type PositionItemsProps = {
  brokerID: string;
  account: string;
  symbol: string;
  symbolName: string;
  accountType: number;
  productType: number;
  cashMargin: number;
  side: number;
  qty: number | null;
  price: number | null;
  currentAmount: number | null;
  currentAmountUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  validationAmount: number | null;
  diffProfitOrLoss: number | null;
  diffProfitOrLossUSD: number | null;
  currencyUnit: string | null;
};

export const ProfitAndlossStuffing = ({
  asset,
  assetBundleType,
  UserBrokerUpdateTime,
  getIsOutOfDate,
  toPreview,
  backHome,
}: ProfitAndLossStuffingProps): React.ReactElement => {
  const {
    data: positions,
    isFetching: positionsIsFetching,
    isRefetching: positionsIsRefetching,
    refetch: positionsRefetch,
  } = useQuery(
    'profitAndLossStuffingPositions',
    () => fetchPositions(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: assetHistory, refetch: assetHistoryRefetch } = useQuery(
    'portfolioStuffingAssetHistory',
    () => fetchAssetHistory(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await positionsRefetch();
      await assetHistoryRefetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBundleType]);

  const title = '保有資産の損益';

  if (
    positions === null ||
    positions === undefined ||
    assetHistory === null ||
    assetHistory === undefined
  ) {
    return (
      <>
        <LoadingChart />
        <LoadingList />
      </>
    );
  }

  return (
    <>
      {positionsIsFetching && positionsIsRefetching ? (
        <LoadingChart />
      ) : (
        <>
          <Chart
            {...{
              assetHistory: assetHistory as AssetHistoryItemsProps[],
              asset,
              UserBrokerUpdateTime,
              toPreview,
              backHome,
            }}
          />
        </>
      )}
      <Position
        {...{
          positions: positions as PositionItemsProps[],
          getIsOutOfDate,
          title,
        }}
      />
    </>
  );
};

const LoadingChart = () => {
  return (
    <div className="pl-4 mb-4 bg-bluegray-600">
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '120px',
          paddingBottom: '120px',
        }}
      />
    </div>
  );
};

const LoadingList = () => {
  return (
    <div className="p-4 pb-0 max-w-md mx-auto">
      <div className="text-lg font-bold mb-6">保有資産の損益</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const fetchPositions = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;

  const resPositions_ = await getPositions(groupID);
  const resPositions = resPositions_.Items.map(item => {
    return {
      brokerID: item.BrokerID,
      account: item.Account,
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      accountType: item.AccountType,
      productType: item.ProductType,
      cashMargin: item.CashMargin,
      side: item.Side,
      qty: item.Qty,
      price: item.Price,
      currentAmount: item.CurrentAmount,
      currentAmountUSD: item.CurrentAmountUSD,
      profitOrLoss: item.ProfitOrLoss,
      profitOrLossUSD: item.ProfitOrLossUSD,
      validationAmount: item.ValidationAmount,
      diffProfitOrLoss: item.DiffProfitOrLoss,
      diffProfitOrLossUSD: item.DiffProfitOrLossUSD,
      currencyUnit: item.CurrencyUnit,
    };
  });
  return resPositions;
};

const fetchAssetHistory = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;

  const resAssetHistory_ = await getAssetHistory(
    undefined,
    undefined,
    groupID,
    true
  );
  const resAssetHistory = resAssetHistory_.Items.map(item => {
    return {
      day: item.Day,
      totalAssetAmount: item.TotalAssetAmount,
      diffTotalAssetAmount: item.DiffTotalAssetAmount,
      diffTotalAssetRate: item.DiffTotalAssetRate,
      marginProfitOrLoss: item.MarginProfitOrLoss,
      totalProfitOrLoss: item.TotalProfitOrLoss,
      diffTotalProfitOrLoss: item.DiffTotalProfitOrLoss,
      diffTotalProfitOrLossRate: item.DiffTotalProfitOrLossRate,
      stockAmount: item.StockAmount,
      marginAmount: item.MarginAmount,
      roboAdvisorAmount: item.RoboAdvisorAmount,
      uSStockAmount: item.USStockAmount,
      investmentTrustAmount: item.InvestmentTrustAmount,
      unlistedStockAmount: item.UnlistedStockAmount,
      etfEtnAmount: item.EtfEtnAmount,
      foreignEtfEtnAmount: item.ForeignEtfEtnAmount,
      reitAmount: item.ReitAmount,
      foreignReitAmount: item.ForeignReitAmount,
      bondAmount: item.BondAmount,
      foreignBondAmount: item.ForeignBondAmount,
      ebAmount: item.EbAmount,
      foreignEbAmount: item.ForeignEbAmount,
      realEstateAmount: item.RealEstateAmount,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
      incorrect: item.Incorrect,
      topix: item.Topix ?? null,
    };
  });
  return resAssetHistory;
};
